import { DestroyRef, Component, Input, OnInit, OnDestroy, ElementRef, inject, ChangeDetectorRef, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService, MultiOrgUser, Organization } from '../../../auth/auth.service';
import { ProfileService } from '../../../profile/profile.service';
import { InstructorOrganizationService } from '../../../instructor/instructor-organization.service';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-organization-switcher',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './organization-switcher.component.html',
  styleUrls: ['./organization-switcher.component.css']
})
export class OrganizationSwitcherComponent implements OnInit, OnDestroy {
  /**
   * Which edge the "Switch Organization" menu is anchored to. KAN-567.
   *
   * This component renders THREE times on a signed-in page: the desktop sidebar,
   * the sidebar inside the collapsed mobile menu, and the top bar. They need
   * opposite anchoring, so this cannot be a stylesheet rule - a bare CSS change
   * hits all three copies.
   *
   *   'start' (default) - left-anchored, correct for the sidebar copies, where
   *     right-anchoring could push the menu off the LEFT edge.
   *   'end' - right-anchored, REQUIRED for the top-bar copy. That copy's toggle
   *     starts ~152px in on a phone, so a left-anchored menu ran from 88px to
   *     392px on a 320px screen and every organization row was off screen.
   */
  @Input() menuAlign: 'start' | 'end' = 'start';

  /**
   * How the ACTIVE organization's name behaves when it does not fit. KAN-624.
   *
   *   'truncate' (default) - one line, ellipsized; the sidebar copies, whose width
   *     is set by the sidebar rather than by the phone.
   *   'wrap' - the whole name, broken onto as many lines as it needs; the top-bar
   *     copy. Ellipsizing there cut "Default Organization" to "Defa..." on phones,
   *     and the picker exists to tell an instructor which workspace they are in.
   */
  @Input() nameOverflow: 'truncate' | 'wrap' = 'truncate';

  get wrapName(): boolean {
    return this.nameOverflow === 'wrap';
  }

  user: MultiOrgUser | null = null;
  currentOrg: Organization | null = null;
  isDropdownOpen = false;
  isLoading = false;
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private clickOutsideListener: ((event: MouseEvent) => void) | null = null;

  constructor(
    private authService: AuthService,
    private profileService: ProfileService,
    private instructorOrgService: InstructorOrganizationService,
    private router: Router,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    // Get initial user
    this.loadUser();

    // Subscribe to organization updates (e.g., when user accepts an invitation)
    this.authService.userOrganizations$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(orgs => orgs !== null) // Skip initial null value
      )
      .subscribe(() => {
        console.log('[OrgSwitcher] Organizations updated, reloading user data');
        this.loadUser();
        this.cdr.markForCheck();
      });
  }

  private loadUser(): void {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.user = currentUser as MultiOrgUser;
      this.updateCurrentOrg();
    }
  }

  ngOnDestroy(): void {
    
    // Remove click outside listener
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener);
    }
  }

  updateCurrentOrg(): void {
    if (this.user && this.user.organizations && this.user.currentOrgId) {
      this.currentOrg = this.user.organizations.find(org => org.id === this.user!.currentOrgId) || null;
    } else {
      this.currentOrg = null;
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      // Register listener outside zone to avoid change detection on every document click.
      // Only re-enter the zone when we actually need to close.
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.clickOutsideListener = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!this.elementRef.nativeElement.contains(target)) {
              this.ngZone.run(() => this.closeDropdown());
            }
          };
          document.addEventListener('click', this.clickOutsideListener);
        }, 0);
      });
    } else {
      this.closeDropdown();
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.removeClickOutsideListener();
    this.cdr.detectChanges();
  }

  private removeClickOutsideListener(): void {
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener);
      this.clickOutsideListener = null;
    }
  }

  async switchOrganization(org: Organization): Promise<void> {
    if (org.id === this.user?.currentOrgId) {
      this.closeDropdown();
      return;
    }

    this.isLoading = true;
    this.closeDropdown();

    // Store previous org ID for rollback if needed
    const previousOrgId = this.user?.currentOrgId;

    try {
      console.log('[OrgSwitcher] Switching to organization:', org.id, org.name);

      // Update default organization on server FIRST
      // This also updates the user profile with new currentOrgRole via AuthService
      console.log('[OrgSwitcher] Updating server and user profile...');
      await this.authService.setDefaultOrganization(org.id).toPromise();
      console.log('[OrgSwitcher] Server and user profile updated');

      // Update local state to reflect the new org in the UI
      if (this.user) {
        this.user.currentOrgId = org.id;
        this.user.currentOrgRole = org.role;
        this.updateCurrentOrg();
      }

      // Update the InstructorOrganizationService AFTER user profile is updated
      // This ensures Dashboard subscription gets the UPDATED user role
      console.log('[OrgSwitcher] Updating InstructorOrganizationService...');
      this.instructorOrgService.setSelectedOrganization(org.id);
      console.log('[OrgSwitcher] InstructorOrganizationService updated');

      // Navigate to dashboard to refresh context
      console.log('[OrgSwitcher] Navigating to dashboard...');
      await this.router.navigate(['/dashboard'], { replaceUrl: true });
      console.log('[OrgSwitcher] Navigation complete');
    } catch (error) {
      console.error('Failed to switch organization:', error);
      // Revert the org service if server update failed
      if (previousOrgId) {
        this.instructorOrgService.setSelectedOrganization(previousOrgId);
      }
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  getRoleDisplay(role: string): string {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'instructor':
        return 'Instructor';
      case 'learner':
        return 'Learner';
      default:
        return role;
    }
  }

  getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'admin':
        return 'badge-error';
      case 'instructor':
        return 'badge-primary';
      case 'learner':
        return 'badge-info';
      default:
        return 'badge-ghost';
    }
  }
}